import { LoadingButton } from '@mui/lab'
import i18n from '@services/i18n/i18n'
import { ConfirmDialog } from '@shared/components/ui/ConfirmDialog'
import { CreateEditLayout } from '@shared/layouts/CreateEditLayout'
import { CreateProfessionInput, useCreateProfessionMutation } from '@typings/graphql'
import React from 'react'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { DefaultSnackbar } from '@shared/components/ui/DefaultSnackbar'
import { useErrorMessage } from '@hooks/useErrorMessage'
import { useUnsavedChanges } from '@hooks/useUnsavedChanges'

import { ProfessionCreateEditInputs } from '../components/ProfessionCreateEditInputs'
import { ProfessionsProvider } from '../provider/ProfessionsProvider'

type ProfessionInputType = Omit<CreateProfessionInput, 'texts'> & {
  texts: Record<string, string>
}

const ProfessionsCreateViewContent: React.FC = () => {
  const { t } = useTranslation()

  const [loading, setLoading] = React.useState(false)
  const [showCreatedDialog, setShowCreatedDialog] = React.useState(false)
  const [showToast, setShowToast] = React.useState(false)
  const [created, setCreated] = React.useState(false)

  const { setMessageByCode, errorMessage } = useErrorMessage()

  const navigate = useNavigate()

  const { formState: { dirtyFields, isDirty, isValid, isSubmitted }, handleSubmit } = useFormContext<ProfessionInputType>()

  useUnsavedChanges(isDirty && !created && (!isSubmitted || !!errorMessage))

  const [createProfession] = useCreateProfessionMutation()

  const onSubmit = handleSubmit(async (input) => {
    setLoading(true)

    try {
      await createProfession({
        variables: {
          data: {
            examDates: input.examDates.map((examDate) => ({
              examDate: examDate.examDate,
              season: examDate.season,
              year: examDate.year
            })),
            texts: Object.keys(input.texts).map((key) => ({
              key,
              lang: i18n.resolvedLanguage,
              value: input.texts[key]
            })),
            config: input.config || {}
          }
        }
      })

      setCreated(true)
      setShowCreatedDialog(true)
    } catch (e) {
      setMessageByCode(e)
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  })

  const handleConfirm = () => {
    navigate('/professions')
  }

  return (
    <CreateEditLayout
      title={t('professions.createTitle')}
      description={t('professions.createDescription')}
      buttonComp={(
        <LoadingButton
          variant="contained"
          type='submit'
          loading={loading}
          onClick={onSubmit}
          disabled={!dirtyFields || !isValid}
        >
          {t('common.save')}
        </LoadingButton>
      )}
    >
      <ProfessionCreateEditInputs />

      <ConfirmDialog
        title={t('common.entryCreated')}
        open={showCreatedDialog}
        hideCancel
        onConfirm={handleConfirm}
      >
        {t('professions.createCompleted')}
      </ConfirmDialog>

      <DefaultSnackbar
        open={showToast}
        severity={'error'}
        onClose={() => setShowToast(false)}
        message={errorMessage}
      />
    </CreateEditLayout>
  )
}

export const ProfessionsCreateView: React.FC = () => {
  const formData = useForm<ProfessionInputType>({
    defaultValues: {
      config: {
        trainingYears: 3
      }
    },
    mode: 'onChange'
  })

  return (
    <ProfessionsProvider>
      <FormProvider {...formData}>
        <ProfessionsCreateViewContent />
      </FormProvider>
    </ProfessionsProvider>
  )
}
